import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Field, Label, Switch } from "@headlessui/react";
import { useAppSelector } from "../../../../app/hooks";
import { convertLongTimeToTotalMinutes } from "../../../../app/datetimeUtils";
import { Loading } from "../../../../components/Loading";
import { SimpleOption } from "../../../../components/input/SimpleListbox";
import Select from "../../../../components/input/Select";
import { useAdminReportsFilters } from "../useAdminReportsFilters";
import { getReportLevel, buildReportQueryString } from "../adminReportsUtil";
import {
	ReportLevel,
	AdminReportsResponse,
	useGetLessonCategoriesForReportsQuery,
} from "../adminReportsApiSlice";
import { DistrictSchoolFilters } from "../DistrictSchoolFilters";
import { TableRightSideHeader } from "../TableRightSideHeader";
import {
	LessonUsageByCategoryAndLessonRow,
	LessonUsageByCategoryRow,
	useLazyLessonUsageByCategoryAndLessonQuery,
	useLazyLessonUsageByCategoryQuery,
} from "./adminUsageReportApiSlice";
import { LessonUsageByCategoryTable } from "./LessonUsageByCategoryTable";
import { LessonUsageByCategoryChart } from "./LessonUsageByCategoryChart";

export default function LessonUsageByCategory() {
	const { aspUrl } = useAppSelector((state) => state.site);
	const {
		maxReportLevel,
		districtOptions,
		schoolOptions,
		districtFilter,
		schoolFilter,
		setDistrictFilter,
		setSchoolFilter,
	} = useAdminReportsFilters();

	const navigate = useNavigate();

	const [byCategoryData, setByCategoryData] =
		useState<AdminReportsResponse<LessonUsageByCategoryRow>>();
	const [byLessonData, setByLessonData] =
		useState<AdminReportsResponse<LessonUsageByCategoryAndLessonRow>>();
	const [lastUpdated, setLastUpdated] = useState<Date>();
	const [hideRows, setHideRows] = useState(false);
	const [hideZeroRows, setHideZeroRows] = useState(false);
	const [reportLevel, setReportLevel] = useState<ReportLevel>(maxReportLevel);

	const [categoryFilter, setCategoryFilter] = useState(0);
	const [categoryOptions, setCategoryOptions] = useState<SimpleOption[]>([]);

	const getCategoriesResult = useGetLessonCategoriesForReportsQuery(undefined);
	const categories = getCategoriesResult.data;

	const [callLessonUsageByCategory, { isLoading: isLoadingByCategory }] =
		useLazyLessonUsageByCategoryQuery();
	const [callLessonUsageByCategoryAndLesson, { isLoading: isLoadingByLesson }] =
		useLazyLessonUsageByCategoryAndLessonQuery();

	useEffect(() => {
		if (categories) {
			const options = categories.map(
				(c) =>
					({
						value: Number(c.value),
						label: c.text,
					}) as SimpleOption
			);
			options.unshift({ value: 0, label: "All Categories" });
			setCategoryOptions(options);
		}
	}, [categories]);

	useEffect(() => {
		// skip if don't have the necessary ids for the max level to prevent auto-fail calls
		if (maxReportLevel === ReportLevel.School && (!districtFilter || !schoolFilter)) {
			return;
		}
		if (maxReportLevel === ReportLevel.District && !districtFilter) {
			return;
		}

		callLessonUsageByCategory(
			{
				districtId: districtFilter === 0 ? undefined : districtFilter,
				schoolId: schoolFilter === 0 ? undefined : schoolFilter,
			},
			true //cache the results
		)
			.unwrap()
			.then((payload) => {
				setByCategoryData(payload.response);
				setLastUpdated(payload.lastUpdateDate);
				setReportLevel(getReportLevel(districtFilter, schoolFilter));
			})
			.catch((error) => {
				console.log(error.data);
				navigate("/adminReports");
			});

		if (categoryFilter) {
			callLessonUsageByCategoryAndLesson(
				{
					tutorialCategoryId: categoryFilter,
					districtId: districtFilter === 0 ? undefined : districtFilter,
					schoolId: schoolFilter === 0 ? undefined : schoolFilter,
				},
				true //cache the results
			)
				.unwrap()
				.then((payload) => {
					setByLessonData(payload.response);
					setLastUpdated(payload.lastUpdateDate);
					setReportLevel(getReportLevel(districtFilter, schoolFilter));
				})
				.catch((error) => {
					console.log(error.data);
					navigate("/adminReports");
				});
		} else {
			// Clear byLessonData when switching back to all categories for loading purposes
			setByLessonData(undefined);
		}
	}, [districtFilter, schoolFilter, categoryFilter]);

	const queryString = useMemo(() => {
		return buildReportQueryString({
			districtFilter,
			schoolFilter,
			categoryFilter,
		});
	}, [districtFilter, schoolFilter, categoryFilter]);

	const enrichedByCategoryRows = useMemo(() => {
		if (!byCategoryData) return [];
		return byCategoryData.rows.map((row) => ({
			...row,
			totalMinutes: convertLongTimeToTotalMinutes(row.timeSpent),
		}));
	}, [byCategoryData]);

	const filteredByCategoryRows = useMemo(() => {
		if (!enrichedByCategoryRows.length) return [];
		if (!hideZeroRows) return enrichedByCategoryRows;
		return enrichedByCategoryRows.filter((row) => row.totalMinutes > 0);
	}, [enrichedByCategoryRows, hideZeroRows]);

	const enrichedByLessonRows = useMemo(() => {
		if (!byLessonData) return [];
		return byLessonData.rows.map((row) => ({
			...row,
			totalMinutes: convertLongTimeToTotalMinutes(row.timeSpent),
		}));
	}, [byLessonData]);

	const filteredByLessonRows = useMemo(() => {
		if (!enrichedByLessonRows.length) return [];
		if (!hideZeroRows) return enrichedByLessonRows;
		return enrichedByLessonRows.filter((row) => row.totalMinutes > 0);
	}, [enrichedByLessonRows, hideZeroRows]);

	return (
		<section>
			<h1>{reportLevel} Lesson Usage by Category</h1>
			<p className="mt-4 print:hidden">
				This report breaks down lesson time by category. Click any category or use the
				filter to see specific lesson data. You can refine your results by hiding unused
				rows and easily print or download the report by clicking the three dots.
			</p>
			<div className="mt-3">
				<Loading loading={!byCategoryData && !byLessonData} />
				{(byCategoryData || byLessonData) && (
					<>
						<div className="flex justify-between items-end gap-x-3">
							<div className="flex flex-wrap gap-3 items-end print:hidden">
								<DistrictSchoolFilters
									districtOptions={districtOptions}
									schoolOptions={schoolOptions}
									districtFilter={districtFilter}
									schoolFilter={schoolFilter}
									setDistrictFilter={setDistrictFilter}
									setSchoolFilter={setSchoolFilter}
								/>
								<Select
									value={categoryFilter}
									setValue={(v) => setCategoryFilter(v as number)}
									label="Category"
									simpleListBoxId="categoryFilter"
									options={categoryOptions}
									fieldClasses="w-full md:flex-0"
									simpleListBoxClasses="w-72"
								/>
								<Field className="w-full md:w-auto flex-none">
									<Label
										as="span"
										className="block mb-2 text-sm font-medium text-gray-90 pt-2 md:pt-0">
										Hide {categoryFilter ? "Lessons" : "Categories"} with No
										Time Spent
									</Label>
									<Switch
										checked={hideZeroRows}
										onChange={setHideZeroRows}
										name="hideZeroRows"
										id="hideZeroRows"
										className="group relative inline-flex h-8 w-14 shrink-0 cursor-pointer rounded-full border-2 border-transparent bg-gray-200 transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-primary-600 focus:ring-offset-2 data-[checked]:bg-primary-600">
										<span
											aria-hidden="true"
											className="pointer-events-none inline-block size-7 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out group-data-[checked]:translate-x-6"
										/>
									</Switch>
								</Field>
							</div>
							<TableRightSideHeader
								lastUpdated={lastUpdated}
								hasData={
									!categoryFilter
										? filteredByCategoryRows.length !== 0
										: filteredByLessonRows.length !== 0
								}
								hideRows={hideRows}
								setHideRows={setHideRows}
								uniqueKey="lesson-usage"
								downloadExcelLink={`${aspUrl}/AdminReports/LessonUsageByCategory${categoryFilter ? "AndLesson" : ""}Excel?${queryString}`}
								downloadCsvLink={`${aspUrl}/AdminReports/LessonUsageByCategory${categoryFilter ? "AndLesson" : ""}Csv?${queryString}`}
							/>
						</div>

						{isLoadingByCategory || isLoadingByLesson ? (
							<div className="mt-4">
								<Loading loading={true} />
							</div>
						) : !categoryFilter && byCategoryData ? (
							<>
								<LessonUsageByCategoryTable
									className="mt-4"
									isByCategory={true}
									fullData={byCategoryData}
									filteredRows={filteredByCategoryRows}
									hideRows={hideRows}
									nameField="tutorialCategoryName"
									nameHeader="Category"
									dataKey="tutorialCategoryId"
									footerLabel="All Categories"
									onNameClick={(rowData) =>
										setCategoryFilter(rowData.tutorialCategoryId)
									}
								/>
								<div className="print:hidden">
									<LessonUsageByCategoryChart
										level={reportLevel}
										data={filteredByCategoryRows}
										hasData={filteredByCategoryRows.length > 0}
										yAxisDataField="tutorialCategoryName"
										title={`${reportLevel} Lesson Usage by Category`}
									/>
								</div>
							</>
						) : (
							byLessonData && (
								<>
									<LessonUsageByCategoryTable
										className="mt-4"
										isByCategory={false}
										fullData={byLessonData}
										filteredRows={filteredByLessonRows}
										hideRows={hideRows}
										nameField="lesson"
										nameHeader="Lesson"
										dataKey="lesson"
										footerLabel="All Lessons"
									/>
									<div className="print:hidden">
										<LessonUsageByCategoryChart
											level={reportLevel}
											data={filteredByLessonRows}
											hasData={filteredByLessonRows.length > 0}
											yAxisDataField="lesson"
											title={`${reportLevel} Lesson Usage by Lesson`}
										/>
									</div>
								</>
							)
						)}
					</>
				)}
			</div>
		</section>
	);
}
